// ===================================================================
// ❌ GERENCIADOR DE MOTIVOS DE REJEIÇÃO - IMPLEMENTAÇÃO COMPLETA
// ===================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { styles } from '../../styles/globalStyles';
import { Alert } from '../../components/ui/Alert';
import { Modal } from '../../components/ui/Modal';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { apiClient } from '../../api/apiClient';
import { validateFields } from '../../utils/helpers';
import { useNotifications } from '../../contexts/NotificationContext';
import {
  REJECTION_REASON_CONTEXTS,
  formatRejectionReasonContexts,
} from '../../constants/rejectionReasonContexts';

export const RejectionReasonsManager = React.memo(({ user }) => {
  const [reasons, setReasons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingReason, setEditingReason] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    is_active: true,
    contexts: [],
  });
  const [error, setError] = useState('');
  const { addNotification } = useNotifications();

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiClient.getRejectionReasons(true);
      setReasons(Array.isArray(data) ? data : []);
    } catch (err) {
      setError('Erro ao carregar motivos de rejeição');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError('');
  }, [error]);

  const toggleContext = useCallback((contextId) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.contexts) ? prev.contexts : [];
      const next = current.includes(contextId)
        ? current.filter((id) => id !== contextId)
        : [...current, contextId];
      return { ...prev, contexts: next };
    });
    if (error) setError('');
  }, [error]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    if (!validateFields({ title: formData.title })) {
      setError('Preencha o título');
      return;
    }

    try {
      setLoading(true);
      if (editingReason) {
        const { error: updateError } = await apiClient.updateRejectionReason(editingReason.id, {
          title: formData.title,
          description: formData.description || '',
          is_active: Boolean(formData.is_active),
          contexts: formData.contexts || [],
        });
        if (updateError) {
          setError(updateError);
        } else {
          addNotification('Sucesso', 'Motivo de rejeição atualizado com sucesso', 'success');
          setShowModal(false);
          setEditingReason(null);
          loadData();
        }
      } else {
        const { error: createError } = await apiClient.createRejectionReason({
          title: formData.title,
          description: formData.description || '',
          is_active: Boolean(formData.is_active),
          contexts: formData.contexts || [],
        });
        if (createError) {
          setError(createError);
        } else {
          addNotification('Sucesso', 'Motivo de rejeição criado com sucesso', 'success');
          setShowModal(false);
          loadData();
        }
      }
    } catch (err) {
      setError('Erro ao salvar motivo de rejeição');
    } finally {
      setLoading(false);
    }
  }, [formData, editingReason, loadData, addNotification]);

  const handleEdit = useCallback((reason) => {
    setEditingReason(reason);
    setFormData({
      title: reason.title || '',
      description: reason.description || '',
      is_active: Boolean(reason.is_active),
      contexts: Array.isArray(reason.contexts) ? reason.contexts : [],
    });
    setShowModal(true);
  }, []);

  const handleDelete = useCallback(async (reasonId) => {
    if (!window.confirm('Tem certeza que deseja excluir este motivo de rejeição?')) {
      return;
    }

    try {
      setLoading(true);
      const { error: deleteError } = await apiClient.deleteRejectionReason(reasonId);
      if (deleteError) {
        setError(deleteError);
      } else {
        addNotification('Sucesso', 'Motivo de rejeição excluído com sucesso', 'success');
        loadData();
      }
    } catch (err) {
      setError('Erro ao excluir motivo de rejeição');
    } finally {
      setLoading(false);
    }
  }, [loadData, addNotification]);

  const handleNewReason = useCallback(() => {
    setEditingReason(null);
            setFormData({
          title: '',
          description: '',
          is_active: true,
          contexts: [],
        });
    setShowModal(true);
  }, []);



  if (loading && reasons.length === 0) {
    return <LoadingSpinner text="Carregando motivos de rejeição..." />;
  }

  return (
    <div style={styles.contentContainer}>
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <h2 style={styles.cardTitle}>❌ Motivos de Indeferimento</h2>
          <button
            onClick={handleNewReason}
            style={{
              ...styles.button,
              ...styles.buttonPrimary
            }}
          >
            ➕ Novo Motivo
          </button>
        </div>

        {error && (
          <Alert 
            message={error} 
            type="error" 
            onClose={() => setError('')} 
          />
        )}

        {reasons.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
            <h3>Nenhum motivo de rejeição encontrado</h3>
            <p>Crie o primeiro motivo para começar</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={styles.table}>
              <thead style={styles.tableHeader}>
                <tr>
                  <th style={styles.tableHeaderCell}>Título</th>
                  <th style={styles.tableHeaderCell}>Descrição</th>
                  <th style={styles.tableHeaderCell}>Tipos de recurso</th>
                  <th style={styles.tableHeaderCell}>Status</th>
                  <th style={styles.tableHeaderCell}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {reasons.map(reason => (
                  <tr key={reason.id} style={styles.tableRow}>
                    <td style={styles.tableCell}>
                      <div style={{ fontWeight: '600', color: '#1F2937' }}>
                        {reason.title}
                      </div>
                    </td>
                    <td style={styles.tableCell}>
                      <div style={{ maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {reason.description}
                      </div>
                    </td>

                    <td style={styles.tableCell}>
                      <div style={{ maxWidth: '280px', fontSize: '13px', color: '#4B5563' }}>
                        {formatRejectionReasonContexts(reason.contexts)}
                      </div>
                    </td>

                    <td style={styles.tableCell}>
                      <span style={{
                        ...styles.statusBadge,
                        ...(reason.is_active ? styles.statusApproved : styles.statusRejected)
                      }}>
                        {reason.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td style={styles.tableCell}>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleEdit(reason)}
                          style={{
                            ...styles.button,
                            ...styles.buttonSecondary,
                            ...styles.buttonSmall
                          }}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={() => handleDelete(reason.id)}
                          style={{
                            ...styles.button,
                            ...styles.buttonDanger,
                            ...styles.buttonSmall
                          }}
                        >
                          🗑️ Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingReason ? 'Editar Motivo de Rejeição' : 'Novo Motivo de Rejeição'}
      >
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Título *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                style={styles.input}
                placeholder="Ex: Documentação incompleta"
                required
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Descrição *</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                style={styles.textarea}
                placeholder="Descrição detalhada do motivo de rejeição..."
                rows="4"
                required
              />
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Tipos de recurso</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                {REJECTION_REASON_CONTEXTS.map((ctx) => (
                  <label key={ctx.id} style={{ display: 'flex', alignItems: 'center', fontSize: '14px', color: '#374151' }}>
                    <input
                      type="checkbox"
                      checked={(formData.contexts || []).includes(ctx.id)}
                      onChange={() => toggleContext(ctx.id)}
                      style={{ marginRight: '8px' }}
                    />
                    {ctx.label}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '8px' }}>
                Mesmos tipos da página Recursos. Se nenhum for marcado, o motivo vale para todos os tipos (compatível com cadastros antigos).
              </div>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleInputChange('is_active', e.target.checked)}
                  style={{ marginRight: '8px' }}
                />
                Motivo ativo
              </label>
              <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '4px' }}>
                Motivos inativos não aparecem nas opções de rejeição
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
            <button
              type="button"
              onClick={() => setShowModal(false)}
              style={{
                ...styles.button,
                ...styles.buttonSecondary
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                ...styles.button,
                ...styles.buttonPrimary,
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading ? 'Salvando...' : (editingReason ? 'Atualizar' : 'Criar')}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}); 